/**
 * TemplateTranslateButton — per-customization-template "Translate" affordance.
 *
 * Reuses the existing Plan 1928 TranslateModal (same one used for Item name /
 * short description / description) to let merchants translate a customization
 * template's display name + each field's question label into the store's
 * enabled languages, so the bundle personalization sheet can render EN/TH.
 *
 * Registry mapping (mirrors Item Attribute — display-only, base string is the key):
 *   ref_doctype = "Item Customization Template"
 *   ref_name    = template docname (stable parent name)
 *   fieldname   = "__template_name__" for the template's own name,
 *                 else the BASE label string of each field (the label is the
 *                 answer/rule key, so it must stay base; the translation is the
 *                 display value).
 */
import { useMemo, useState } from "react";
import { Languages } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  TranslateModal,
  type TranslatableFieldDef,
} from "@/features/inventory/item-detail/components/TranslateModal";
import { useStorefrontTenantSettings } from "@/features/storefront/settings/useStorefrontTenantSettings";

// Reserved sentinel — MUST match CUSTOMIZATION_TEMPLATE_NAME_FIELD in the backend
// (zaviago_backend/storefront/translation_helpers.py).
const TEMPLATE_NAME_FIELD = "__template_name__";

interface TemplateTranslateButtonProps {
  /** Template docname (Item Customization Template.name) — the translation ref_name. */
  templateDocname: string;
  /** Base template display name (template_name). */
  templateDisplayName: string;
  /** Fields whose question labels are translatable. */
  fields: Array<{ label?: string | null }>;
  className?: string;
}

export function TemplateTranslateButton({
  templateDocname,
  templateDisplayName,
  fields,
  className,
}: TemplateTranslateButtonProps) {
  const [open, setOpen] = useState(false);
  const { settings } = useStorefrontTenantSettings();

  const enabledLanguageCount = useMemo(() => {
    try {
      const parsed = JSON.parse(settings?.enabled_languages || "[]");
      return Array.isArray(parsed) ? parsed.length : 0;
    } catch {
      return 0;
    }
  }, [settings?.enabled_languages]);

  const translatableFields = useMemo<TranslatableFieldDef[]>(() => {
    const out: TranslatableFieldDef[] = [
      {
        fieldname: TEMPLATE_NAME_FIELD,
        label: "Template name",
        baseValue: templateDisplayName || "",
        multiline: false,
      },
    ];
    const seen = new Set<string>();
    for (const f of fields) {
      const base = (f.label || "").trim();
      if (!base || seen.has(base)) continue; // dedupe: label is the stable key
      seen.add(base);
      out.push({
        fieldname: base,
        label: `Question — ${base}`,
        baseValue: base,
        multiline: false,
      });
    }
    return out;
  }, [templateDisplayName, fields]);

  // Only show when the store has >=2 languages and a saved template to key off.
  if (!templateDocname || enabledLanguageCount < 2) return null;

  return (
    <>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className={`h-7 gap-1.5 px-2 text-xs text-muted-foreground ${className ?? ""}`}
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
          setOpen(true);
        }}
      >
        <Languages className="h-3.5 w-3.5" />
        Translate
      </Button>
      <TranslateModal
        open={open}
        onOpenChange={setOpen}
        refDoctype="Item Customization Template"
        refName={templateDocname}
        fields={translatableFields}
      />
    </>
  );
}
